import { recalculateSessionAsync } from '../api/api.js';
import { showSessionExpiredModal } from './modal-functions.js';
import { showModal } from './ui.js';

// Matches $config['sess_expiration'] for /checkout routes in application/config/config.php
const SESSION_DURATION_SECONDS = 60 * 20;

// Show the "stay logged in" prompt when this many seconds are left
const WARNING_THRESHOLD_SECONDS = 60 * 2;

export class Timer extends HTMLElement {
    static get observedAttributes() {
        return ['style', 'fontSize'];
    }

    constructor() {
        super();

        const shadow = this.attachShadow({ mode: 'open'});
        const MILLISECONDS = 1000;

        this._style = `font-weight: normal; color: var(--secondary-text-color);`;
        this._fontSize = `font-size: var(--font-size-md)`;
        this._tickRate = MILLISECONDS * 1;

        this.time = SESSION_DURATION_SECONDS;
        this._warningShown = false;

        shadow.innerHTML = `
            <div>
                <span style="${this._style} ${this._fontSize}">
                    ${this.getTimerDisplayString(this.time)}
                </span>
            </div>
        `;

        this._onSessionActivity = () => this.resetTimer();
    }

    // Fires when added to DOM
    connectedCallback() {
        this._timerText = this.shadowRoot.querySelector('span');
        this.timerTick();

        // Any successful request to the server refreshes the PHP session's last activity time,
        // so reset the visual countdown to stay in sync with the real session timeout
        window.addEventListener('atb:session-activity', this._onSessionActivity);
    }

    // Fires when removed from DOM
    disconnectedCallback() {
        clearTimeout(this._timeoutId);
        window.removeEventListener('atb:session-activity', this._onSessionActivity);
    }

    attributeChangedCallback() {
        const style = this.getAttribute('style');
        if (style) this._style += style;

        const passedFontSize = this.getAttribute('fontSize');
        const fontSize = this.hasFontSize(passedFontSize) ? passedFontSize : 'md';
        this._fontSize = `font-size: var(--font-size-${fontSize})`;

        if (this._timerText) {
            this._timerText.setAttribute('style', `${this._style} ${this._fontSize}`);
        }
    }

    timerTick() {
        this._timeoutId = setTimeout(() => {
            this.time--;
            this._timerText.textContent = this.getTimerDisplayString(this.time);

            if (this.time <= 0) {
                showSessionExpiredModal();
                return;
            }

            if (this.time === WARNING_THRESHOLD_SECONDS) {
                this.showExtendSessionPrompt();
            }

            this.timerTick();
        }, this._tickRate);
    }

    resetTimer() {
        this.time = SESSION_DURATION_SECONDS;
        this._warningShown = false;

        if (this._timerText) {
            this._timerText.textContent = this.getTimerDisplayString(this.time);
        }
    }

    showExtendSessionPrompt() {
        if (this._warningShown) return;
        this._warningShown = true;

        showModal(
            'Are you still there?',
            'Your session is about to expire due to inactivity. Would you like to stay logged in and continue your booking?',
            {
                buttonText: 'Stay logged in',
                buttonCallback: () => this.extendSession(),
                buttonOpts: { once: true }
            }
        );
    }

    async extendSession() {
        await recalculateSessionAsync();
        this.resetTimer();
    }

    getTimerDisplayString(t) {
        const time = Math.max(t, 0);
        const mins = Math.floor(time / 60);
        const minsDisplayString = mins > 0 ? mins : 0;

        const secs = time % 60;

        const secsDisplayString = secs > 9 ? secs : '0'+secs;

        return `Time Left: ${minsDisplayString}:${secsDisplayString}`;
    }

    hasFontSize(fontSizeId) {
        const fontSizeSet = new Set(['sm', 'lg', 'md', 'xl', 'xs']);
        return fontSizeSet.has(fontSizeId);
    }
}

customElements.define("session-timer", Timer);
